import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import { downloadVideo } from '@/lib/download'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { track } from '@/lib/track'
import { Download, Loader2, Link2, Check, Sparkles, ArrowRight, Code2, Sun, Moon, Copy } from 'lucide-react'
import { PlyrPlayer } from '@/components/video/PlyrPlayer'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { LOGO_URL, OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

interface SharedVideo {
  title: string | null
  prompt: string | null
  description: string | null
  videoUrl: string | null
  thumbnailUrl: string | null
  duration: number
  aspectRatio?: string
  createdAt: number
  isLegacy?: boolean
}

function ThemeToggle({ dark, onToggle }: { dark: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'relative flex items-center w-16 h-8 rounded-full p-1 transition-colors duration-300',
        dark ? 'bg-neutral-800 ring-1 ring-white/10' : 'bg-neutral-200 ring-1 ring-black/5'
      )}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <span
        className={cn(
          'absolute flex items-center justify-center w-6 h-6 rounded-full transition-all duration-300 shadow-sm',
          dark
            ? 'translate-x-8 bg-neutral-700'
            : 'translate-x-0 bg-white'
        )}
      >
        {dark ? (
          <Moon className="w-3.5 h-3.5 text-neutral-300" />
        ) : (
          <Sun className="w-3.5 h-3.5 text-amber-500" />
        )}
      </span>
    </button>
  )
}

const DEMO_SLUGS: Record<string, string> = {
  'dolphin-ocean-journey': 'kh700hk6s9mzspkbcv8atbpaqh80vzqt',
}

export default function SharePage({ demo }: { demo?: boolean } = {}) {
  const { id: paramId } = useParams<{ id: string }>()
  const id = demo ? DEMO_SLUGS[window.location.pathname.split('/').pop() || ''] : paramId
  const [video, setVideo] = useState<SharedVideo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const shareTracked = useRef(false)
  useEffect(() => {
    if (video && id && !shareTracked.current) {
      shareTracked.current = true
      track('share.page_viewed', { videoId: id })
    }
  }, [video, id])
  const [copied, setCopied] = useState(false)
  const [embedOpen, setEmbedOpen] = useState(false)
  const [embedCopied, setEmbedCopied] = useState(false)
  const [embedAutoplay, setEmbedAutoplay] = useState(false)
  const [embedMuted, setEmbedMuted] = useState(false)
  const [embedLoop, setEmbedLoop] = useState(false)
  const [embedControls, setEmbedControls] = useState(true)
  const [dark, setDark] = useState(false)

  const isConvexId = id && id.length > 20
  // Short IDs (6 chars) could be workflow shortcodes or legacy video shortcodes
  const isShortId = id && !isConvexId

  const convexVideo = useQuery(
    api.videos.getPublic,
    isConvexId ? { id: id as Id<'videos'> } : 'skip'
  )

  // Try resolving as workflow shortcode
  const workflowData = useQuery(
    api.workflows.getByShortcodePublic,
    isShortId ? { shortcode: id } : 'skip'
  )

  const getLegacyVideo = useAction(api.legacyVideos.getPublicLegacyVideo)

  useEffect(() => {
    async function fetchVideo() {
      if (!id) {
        setError('No video ID provided')
        setLoading(false)
        return
      }

      // Convex video ID (long ID)
      if (isConvexId) {
        if (convexVideo !== undefined) {
          if (convexVideo) {
            setVideo({
              title: convexVideo.title ?? null,
              prompt: convexVideo.prompt ?? null,
              description: convexVideo.prompt ?? null,
              videoUrl: convexVideo.videoUrl ?? null,
              thumbnailUrl: convexVideo.thumbnailUrl ?? null,
              duration: convexVideo.duration,
              aspectRatio: convexVideo.aspectRatio,
              createdAt: convexVideo.createdAt,
              isLegacy: false,
            })
          } else {
            setError('Video not found')
          }
          setLoading(false)
        }
        return
      }

      // Short ID: try workflow shortcode first, then legacy video
      if (isShortId) {
        // Still loading workflow query
        if (workflowData === undefined) return

        if (workflowData && workflowData.finalVideoUrl) {
          setVideo({
            title: workflowData.title ?? null,
            prompt: null,
            description: workflowData.narrative || null,
            videoUrl: workflowData.finalVideoUrl,
            thumbnailUrl: workflowData.thumbnailUrl ?? null,
            duration: 0,
            aspectRatio: workflowData.settings?.aspectRatio,
            createdAt: workflowData.createdAt,
            isLegacy: false,
          })
          setLoading(false)
          return
        }

        // Not a workflow shortcode (or no final video) — try legacy
        try {
          const legacyVideo = await getLegacyVideo({ shortcode: id })
          if (legacyVideo) {
            setVideo({
              title: legacyVideo.title,
              prompt: legacyVideo.prompt,
              description: legacyVideo.prompt,
              videoUrl: legacyVideo.videoUrl,
              thumbnailUrl: legacyVideo.thumbnailUrl,
              duration: legacyVideo.duration,
              aspectRatio: legacyVideo.aspectRatio,
              createdAt: legacyVideo.createdAt,
              isLegacy: true,
            })
          } else {
            setError('Video not found')
          }
        } catch (err) {
          console.error('Error fetching legacy video:', err)
          setError('Video not found')
        }
        setLoading(false)
      }
    }

    fetchVideo()
  }, [id, isConvexId, isShortId, convexVideo, workflowData, getLegacyVideo])

  const handleDownload = useCallback(async () => {
    if (!video?.videoUrl) return
    try {
      await downloadVideo(video.videoUrl, `${video.title || video.prompt || 'video'}.mp4`)
    } catch (err) {
      console.error('Failed to download video:', err)
    }
  }, [video])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = window.location.href
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [])

  const embedCode = useMemo(() => {
    const params = new URLSearchParams()
    if (embedAutoplay) params.set('autoplay', '1')
    if (embedMuted) params.set('muted', '1')
    if (embedLoop) params.set('loop', '1')
    if (!embedControls) params.set('controls', '0')
    const qs = params.toString()
    const embedUrl = `${window.location.origin}/embed/${id}${qs ? `?${qs}` : ''}`
    return `<iframe src="${embedUrl}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`
  }, [id, embedAutoplay, embedMuted, embedLoop, embedControls])

  const handleCopyEmbed = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = embedCode
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    }
  }, [embedCode])

  // Loading state
  if (loading) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center transition-colors duration-300', dark ? 'bg-black' : 'bg-neutral-50')}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className={cn('h-8 w-8 animate-spin', dark ? 'text-neutral-500' : 'text-neutral-400')} />
          <span className={cn('text-sm', dark ? 'text-neutral-500' : 'text-neutral-400')}>Loading video...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !video) {
    return (
      <div className={cn('min-h-screen flex flex-col transition-colors duration-300', dark ? 'bg-black' : 'bg-neutral-50')}>
        {!demo && (
          <header className="px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/">
              <img
                src={LOGO_URL}
                alt="Footage"
                className="h-8 w-8"
              />
            </Link>
            <ThemeToggle dark={dark} onToggle={() => setDark(d => !d)} />
          </header>
        )}
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-4">
          <div className={cn('w-16 h-16 rounded-full flex items-center justify-center', dark ? 'bg-neutral-900/50' : 'bg-neutral-200')}>
            <svg className={cn('w-8 h-8', dark ? 'text-neutral-600' : 'text-neutral-400')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <div className="text-center">
            <h1 className={cn('text-xl font-semibold mb-2', dark ? 'text-white' : 'text-neutral-900')}>Video not found</h1>
            <p className={cn('text-sm', dark ? 'text-neutral-500' : 'text-neutral-500')}>This video may have been removed or the link is incorrect.</p>
          </div>
          <Link
            to="/"
            className={cn(
              'mt-2 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full transition-colors',
              dark ? 'text-white bg-neutral-800 hover:bg-neutral-700' : 'text-neutral-700 bg-neutral-200 hover:bg-neutral-300'
            )}
          >
            Go to Footage
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  const videoTitle = video.title || video.prompt || 'Untitled Video'
  const videoDescription = video.description
    ? (video.description.length > 160 ? video.description.slice(0, 157) + '...' : video.description)
    : `Watch "${videoTitle}" — created with Footage, the AI video creation studio.`
  const canonicalUrl = siteUrl(`/share/${id}`)
  const ogImage = video.thumbnailUrl || OG_IMAGE_URL

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: videoTitle,
    description: video.description || videoDescription,
    thumbnailUrl: video.thumbnailUrl || undefined,
    contentUrl: video.videoUrl || undefined,
    uploadDate: new Date(video.createdAt).toISOString(),
    ...(video.duration > 0 ? { duration: `PT${Math.round(video.duration)}S` } : {}),
    publisher: {
      '@type': 'Organization',
      name: 'Footage',
      logo: { '@type': 'ImageObject', url: LOGO_URL },
      url: siteUrl(),
    },
  }

  const shareHelmet = (
    <Helmet>
      <title>{videoTitle} - Footage</title>
      <meta name="description" content={videoDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="video.other" />
      <meta property="og:title" content={`${videoTitle} - Footage`} />
      <meta property="og:description" content={videoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      {video.videoUrl && <meta property="og:video" content={video.videoUrl} />}
      {video.videoUrl && <meta property="og:video:type" content="video/mp4" />}
      <meta name="twitter:card" content="player" />
      <meta name="twitter:title" content={`${videoTitle} - Footage`} />
      <meta name="twitter:description" content={videoDescription} />
      <meta name="twitter:image" content={ogImage} />
      {video.videoUrl && <meta name="twitter:player" content={siteUrl(`/embed/${id}`)} />}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )

  const isPortrait = video.aspectRatio === '9:16'
  const isSquare = video.aspectRatio === '1:1'

  const videoMaxWidth = isPortrait
    ? 'max-w-[360px]'
    : isSquare
      ? 'max-w-[600px]'
      : 'max-w-[960px]'


  return (
    <div className={cn('min-h-screen flex flex-col transition-colors duration-300', dark ? 'bg-black' : 'bg-neutral-50')}>
      {shareHelmet}
      {/* Header */}
      {!demo && (
        <header className="px-4 sm:px-6 py-4 flex items-center justify-between shrink-0">
          <Link to="/">
            <img
              src={LOGO_URL}
              alt="Footage"
              className={cn('h-8 w-8 transition-opacity', dark ? 'opacity-50 hover:opacity-100' : '')}
            />
          </Link>
          <ThemeToggle dark={dark} onToggle={() => setDark(d => !d)} />
        </header>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-0 sm:px-4 py-0 sm:py-10">
        <div className={`w-full ${videoMaxWidth}`}>
          {/* Video player */}
          <div className={cn(
            'share-player relative overflow-hidden bg-black',
            dark ? '' : 'shadow-2xl shadow-black/20'
          )}>
            {video.videoUrl && (
              <PlyrPlayer
                src={video.videoUrl}
                poster={video.thumbnailUrl || undefined}
                autoPlay={false}
                muted={false}
              />
            )}
            <style>{`
              .share-player .plyr { border-radius: 0 !important; }
              .share-player .plyr-wrapper {
                --plyr-color-main: rgba(255,255,255,0.8);
                --plyr-video-control-color: rgba(255,255,255,0.7);
                --plyr-video-control-background-hover: rgba(255,255,255,0.15);
              }
              .share-player .plyr-wrapper .plyr__control--overlaid {
                background: rgba(255,255,255,0.15) !important;
                backdrop-filter: blur(8px);
                padding: 28px !important;
              }
              .share-player .plyr-wrapper .plyr__control--overlaid svg {
                width: 28px !important;
                height: 28px !important;
              }
              .share-player .plyr-wrapper .plyr__control--overlaid:hover {
                background: rgba(255,255,255,0.25) !important;
              }
              .share-player .plyr-wrapper .plyr--full-ui input[type=range] {
                color: rgba(255,255,255,0.8) !important;
              }
            `}</style>
          </div>

          {/* Video info + actions */}
          <div className="mt-3 sm:mt-4 px-4 sm:px-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {/* Title */}
                <h1 className={cn('text-base sm:text-lg font-medium leading-snug', dark ? 'text-neutral-300' : 'text-neutral-900')}>
                  {videoTitle}
                </h1>

                {/* Date */}
                {!demo && (
                  <div className={cn('mt-1 text-xs', dark ? 'text-neutral-600' : 'text-neutral-400')}>
                    {new Date(video.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={handleCopyLink}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors',
                    dark
                      ? 'text-neutral-500 hover:text-neutral-300'
                      : 'text-neutral-500 hover:text-neutral-700 bg-neutral-100 hover:bg-neutral-200'
                  )}
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Link2 className="w-3.5 h-3.5" />
                      Copy link
                    </>
                  )}
                </button>
                <button
                  onClick={() => setEmbedOpen(true)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors',
                    dark
                      ? 'text-neutral-500 hover:text-neutral-300'
                      : 'text-neutral-500 hover:text-neutral-700 bg-neutral-100 hover:bg-neutral-200'
                  )}
                >
                  <Code2 className="w-3.5 h-3.5" />
                  Embed
                </button>
                <button
                  onClick={handleDownload}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors',
                    dark
                      ? 'text-neutral-500 hover:text-neutral-300'
                      : 'text-neutral-500 hover:text-neutral-700 bg-neutral-100 hover:bg-neutral-200'
                  )}
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            </div>

          </div>

          {/* CTA card (demo pages only) */}
          {demo && (
            <div className={cn(
              'mt-8 sm:mt-10 p-5 sm:p-6 rounded-2xl relative transition-colors duration-300',
              dark
                ? 'bg-linear-to-br from-neutral-900 to-neutral-900/50 ring-1 ring-white/[0.07]'
                : 'bg-linear-to-br from-neutral-100 to-neutral-50 ring-1 ring-neutral-200 shadow-sm'
            )}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 pr-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sparkles className={cn('w-6 h-6 shrink-0', dark ? 'text-teal-400' : 'text-teal-600')} />
                    <span className={cn('text-xl font-bold', dark ? 'text-teal-400' : 'text-teal-600')}>
                      Created with Footage.com<span className="text-[11px] align-super">™</span>
                    </span>
                  </div>
                  <p className={cn('text-base leading-relaxed', dark ? 'text-neutral-400' : 'text-neutral-500')}>
                    Bring your ideas to life with AI. Start for free.
                  </p>
                </div>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-linear-to-r from-teal-600 to-cyan-500 rounded-full hover:from-teal-700 hover:to-cyan-600 transition-all shrink-0 whitespace-nowrap"
                >
                  Get started
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Embed dialog */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className={cn(
          'sm:max-w-md',
          dark
            ? 'bg-neutral-900 border-neutral-800 text-white'
            : 'bg-white border-neutral-200 text-neutral-900'
        )}>
          <DialogHeader>
            <DialogTitle className={dark ? 'text-white' : 'text-neutral-900'}>Embed Video</DialogTitle>
            <DialogDescription className={dark ? 'text-neutral-400' : 'text-neutral-500'}>
              Customize the embed player and copy the code.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className={cn('text-sm', dark ? 'text-neutral-300' : 'text-neutral-700')}>Autoplay</label>
                <Switch
                  checked={embedAutoplay}
                  onCheckedChange={(checked) => {
                    setEmbedAutoplay(checked)
                    if (checked && !embedMuted) setEmbedMuted(true)
                  }}
                  className={cn(
                    dark
                      ? 'data-[state=checked]:bg-teal-600 data-[state=unchecked]:bg-neutral-700'
                      : 'data-[state=checked]:bg-teal-600'
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <label className={cn('text-sm', dark ? 'text-neutral-300' : 'text-neutral-700')}>Muted</label>
                <Switch
                  checked={embedMuted}
                  onCheckedChange={setEmbedMuted}
                  disabled={embedAutoplay}
                  className={cn(
                    dark
                      ? 'data-[state=checked]:bg-teal-600 data-[state=unchecked]:bg-neutral-700'
                      : 'data-[state=checked]:bg-teal-600'
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <label className={cn('text-sm', dark ? 'text-neutral-300' : 'text-neutral-700')}>Loop</label>
                <Switch
                  checked={embedLoop}
                  onCheckedChange={setEmbedLoop}
                  className={cn(
                    dark
                      ? 'data-[state=checked]:bg-teal-600 data-[state=unchecked]:bg-neutral-700'
                      : 'data-[state=checked]:bg-teal-600'
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <label className={cn('text-sm', dark ? 'text-neutral-300' : 'text-neutral-700')}>Show controls</label>
                <Switch
                  checked={embedControls}
                  onCheckedChange={setEmbedControls}
                  className={cn(
                    dark
                      ? 'data-[state=checked]:bg-teal-600 data-[state=unchecked]:bg-neutral-700'
                      : 'data-[state=checked]:bg-teal-600'
                  )}
                />
              </div>
              {embedAutoplay && (
                <p className={cn('text-xs', dark ? 'text-neutral-500' : 'text-neutral-400')}>
                  Browsers require muted audio for autoplay to work.
                </p>
              )}
            </div>

            {/* Code preview */}
            <div className={cn(
              'rounded-lg p-3 text-xs font-mono break-all leading-relaxed',
              dark ? 'bg-neutral-950 text-neutral-400 ring-1 ring-white/5' : 'bg-neutral-100 text-neutral-600 ring-1 ring-black/5'
            )}>
              {embedCode}
            </div>

            {/* Copy button */}
            <button
              onClick={handleCopyEmbed}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                embedCopied
                  ? 'bg-teal-600 text-white'
                  : dark
                    ? 'bg-white text-neutral-900 hover:bg-neutral-200'
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
              )}
            >
              {embedCopied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Embed Code
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer (demo only) */}
      {demo && (
        <footer className="w-full px-6 py-4 border-t border-neutral-100 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/resources" className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors">Resources</Link>
              <Link to="/support" className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors">Support Center</Link>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}
